import React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Layers,
  Target,
  GitBranch,
  Mail,
  Users,
  Trophy,
  Calendar,
  FileText,
  DollarSign,
  ClipboardCheck,
  Play,
  ChevronDown,
} from "lucide-react";

// P3 Checkpoint 2: consolidates the original 12 peer-level War Room
// destinations into 5 primary groups. `value` is the same activeTab value
// Checkpoint 1 already used (URL persistence, handleTabChange,
// handleOperatorNavigate's tabMap, and every TabsContent panel all key off
// these values unchanged) — this file only changes how each is reached,
// never what it is.
export const WARROOM_NAV_GROUPS = [
  {
    id: "overview",
    label: "Overview",
    icon: Layers,
    items: [{ value: "overview", label: "Overview", icon: Layers }],
  },
  {
    id: "marketing",
    label: "Marketing",
    icon: Target,
    items: [
      { value: "campaigns", label: "Campaigns", icon: Target, titleKey: "campaigns" },
      { value: "funnels", label: "Funnels", icon: GitBranch, testId: "funnels-tab" },
      { value: "email", label: "Email", icon: Mail, testId: "email-tab" },
    ],
  },
  {
    id: "agents",
    label: "Agents",
    icon: Users,
    items: [
      { value: "agents", label: "Agent Overview", icon: Trophy, testId: "agents-tab" },
      { value: "booking", label: "Booking Agents", icon: Calendar },
      { value: "sales", label: "Sales Agents", icon: Users },
      { value: "scripts", label: "Scripts", icon: FileText, testId: "scripts-tab", titleKey: "scripts" },
    ],
  },
  {
    id: "financials",
    label: "Financials",
    icon: DollarSign,
    items: [
      { value: "revenue", label: "Revenue", icon: DollarSign, testId: "revenue-tab" },
      { value: "profit-paths", label: "Profit Paths", icon: GitBranch, testId: "profit-paths-tab", titleKey: "profit_paths" },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    icon: ClipboardCheck,
    items: [
      { value: "operator", label: "Operator", icon: ClipboardCheck, testId: "operator-tab" },
      { value: "actions", label: "Actions", icon: Play, testId: "actions-tab" },
    ],
  },
];

export function findActiveGroupId(activeTab) {
  const found = WARROOM_NAV_GROUPS.find((g) => g.items.some((i) => i.value === activeTab));
  return found?.id || "overview";
}

// Checkpoint 3.3: replaced the five equal-width, bordered/filled "card"
// buttons with a flatter, naturally-sized top-nav treatment — no border,
// no background fill by default, and a restrained bottom-border indicator
// (plus brighter text) for the active group, instead of a filled pill.
// Icons use lucide's default currentColor stroke, so they pick up the
// active indigo text color automatically without any extra icon-specific
// class.
//
// Checkpoint 3.4: the flat treatment stayed, but the five groups were left
// clustered at the natural (left) width of their content and read as too
// small/weak. `flex-1` on each button distributed the five groups evenly
// across the full row width, text/icon sizes were bumped one step, and the
// active state gained a subtle indigo surface tint alongside the existing
// border+text color change.
//
// Checkpoint 3.4B: superseded the 3.3/3.4 bordered/underlined treatment
// entirely with a premium application-shell nav — icon-above-label
// composition, no enclosing card/pill/border/underline of any kind, and a
// soft indigo glow (not a line) beneath the active group. `flex-1` still
// drives the even five-zone distribution; only the per-item visual
// treatment changed. Icons remain currentColor, so the active/hover text
// color change still recolors them with no separate icon class.
//
// Checkpoint 3.4C: the 3.4B glow was effectively invisible in the real
// Emergent preview. Root cause, characterized before touching anything:
// the button had `relative` but no explicit z-index of its own, so it
// never established its own CSS stacking context; a bare `relative`
// (without a non-auto z-index/isolation) does NOT create one. The glow's
// `-z-10` therefore didn't stay "one layer behind this button's own
// content" — it escaped to compete against whatever stacking context an
// ANCESTOR happened to establish (a card's `backdrop-blur`, a parent's own
// z-index, etc.), which could easily paint the page's own background over
// it, making the glow disappear entirely depending on what markup
// surrounds the nav on a given page. Separately, even with the escape
// fixed, a `position:absolute` element with `z-index:auto` still paints
// ABOVE plain static content in the same stacking context by CSS's normal
// painting order — so a naive fix could have inverted the problem and
// buried the icon/label under the glow instead.
//
// The fix uses `isolate` (CSS `isolation: isolate`) on the button itself
// to force a real, local, self-contained stacking context — nothing about
// this button's internal layering can ever again depend on what an
// ancestor happens to do. Inside that isolated context, the glow gets an
// explicit `z-0` (no negative value, nothing to escape with) and the
// icon+label are wrapped in their own `relative z-10` span, so which layer
// paints on top is settled by an explicit, unambiguous z-index comparison
// (10 > 0) rather than the static-vs-positioned default rule.
//
// The glow itself also moved from a blurred solid shape (`rounded-full
// bg-indigo-500/40 blur-2xl`) to a radial-gradient background. A gradient
// has no edge to begin with — it's defined as a colour-to-transparent
// falloff — so there's no reliance on blur-filter rendering/GPU
// compositing to soften a shape's boundary, and no risk of it reading as
// a discrete oval/pill under different rendering conditions. It's
// anchored at the zone's bottom-center and spans the full zone width
// (`inset-x-0`) so it scales automatically with each flex-1 zone's actual
// rendered width instead of a fixed px guess.
const baseButtonClass =
  "group isolate relative flex flex-1 flex-col items-center justify-center gap-1.5 px-2 py-3 text-slate-400 transition-colors hover:text-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 rounded-lg";
const activeButtonClass = "text-indigo-200 hover:text-indigo-100";
const labelClass = "text-xs font-semibold uppercase tracking-wide";
const glowBackground =
  "bg-[radial-gradient(ellipse_60%_100%_at_50%_100%,rgba(129,140,248,0.38)_0%,rgba(129,140,248,0.16)_45%,transparent_75%)]";

// Hover reuses the exact same glow element at a lower opacity so hover
// reads as a quieter version of selection, never a stronger one — the
// opacity ceiling for an inactive item's hover (30%) is well under the
// active item's fixed 100%.
function glowClass(isActiveGroup) {
  return isActiveGroup
    ? "opacity-100"
    : "opacity-0 group-hover:opacity-30 transition-opacity duration-200";
}

export default function WarRoomPrimaryNav({ activeTab, onNavigate, helperDefinitions = {} }) {
  const activeGroupId = findActiveGroupId(activeTab);

  return (
    <div className="flex w-full flex-wrap items-stretch gap-1" data-testid="warroom-primary-nav">
      {WARROOM_NAV_GROUPS.map((group) => {
        const GroupIcon = group.icon;
        const isActiveGroup = group.id === activeGroupId;
        const buttonClass = `${baseButtonClass} ${isActiveGroup ? activeButtonClass : ""}`;
        const glow = (
          <span
            aria-hidden="true"
            data-testid={`warroom-nav-${group.id}-glow`}
            className={`pointer-events-none absolute inset-x-0 bottom-0 z-0 h-14 ${glowBackground} ${glowClass(
              isActiveGroup
            )}`}
          />
        );

        if (group.items.length === 1) {
          const only = group.items[0];
          return (
            <button
              key={group.id}
              type="button"
              onClick={() => onNavigate(only.value)}
              className={buttonClass}
              data-testid={`warroom-nav-${group.id}`}
              aria-current={isActiveGroup ? "page" : undefined}
            >
              {glow}
              <span className="relative z-10 flex flex-col items-center gap-1.5">
                <GroupIcon className="h-6 w-6" />
                <span className={labelClass}>{group.label}</span>
              </span>
            </button>
          );
        }

        return (
          <DropdownMenu key={group.id}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={buttonClass}
                data-testid={`warroom-nav-${group.id}`}
                aria-current={isActiveGroup ? "page" : undefined}
              >
                {glow}
                <span className="relative z-10 flex flex-col items-center gap-1.5">
                  <GroupIcon className="h-6 w-6" />
                  <span className={`flex items-center gap-1 ${labelClass}`}>
                    {group.label}
                    <ChevronDown className="h-3 w-3 opacity-50 group-hover:opacity-80" />
                  </span>
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-56 bg-slate-950 text-slate-100 ring-1 ring-white/10"
              data-testid={`warroom-nav-${group.id}-menu`}
            >
              <DropdownMenuLabel className="text-xs text-slate-400 uppercase">
                {group.label}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              {group.items.map((item) => {
                const ItemIcon = item.icon;
                const isActiveItem = item.value === activeTab;
                return (
                  <DropdownMenuItem
                    key={item.value}
                    onClick={() => onNavigate(item.value)}
                    className={`cursor-pointer gap-2 hover:bg-white/10 focus:bg-white/10 ${
                      isActiveItem ? "bg-indigo-500/20 text-indigo-300" : ""
                    }`}
                    data-testid={item.testId || `warroom-nav-item-${item.value}`}
                    title={item.titleKey ? helperDefinitions[item.titleKey] : undefined}
                  >
                    <ItemIcon className="h-4 w-4" />
                    {item.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      })}
    </div>
  );
}
